/**
 * @fileoverview WebSocket error notification and reconnection handling.
 * Displays user notifications for connection issues and monitors reconnection status.
 */

import { isGraphQLWebsocketConnected } from "@/store/api";
import * as Sentry from "@sentry/browser";
import { Message } from "element-ui";

const CHECK_STATUS_INTERVAL_TIME = 1000;
const WEBSOCKET_RECONNECTING_MSG = "Attempt reconnection...";
const WEBSOCKET_CONNECTED_MSG = "Connected";

let isShowMessageBox = false;
let reconnectingMessageBox: any = null;
let connectedMessageBox: any = null;
let checkingStatusInterval: any = null;
let errorMessageDelayTimeout: any = null;

/**
 * Displays a reconnection notification when WebSocket connection fails.
 * Shows warning message after 5 seconds if connection remains down.
 * Monitors connection status and triggers reconnected notification when restored.
 *
 * @param {string | Error} error - The WebSocket error that occurred
 *
 * @example
 * notifyWebsocketError(new Error('Connection lost'));
 */
export const notifyWebsocketError = (error: string | Error) => {
  if (!isShowMessageBox) {
    if (errorMessageDelayTimeout) {
      clearTimeout(errorMessageDelayTimeout);
    }

    errorMessageDelayTimeout = setTimeout(() => {
      if (!isGraphQLWebsocketConnected()) {
        reconnectingMessageBox = Message({
          message: WEBSOCKET_RECONNECTING_MSG,
          type: "warning",
          showClose: false,
          duration: 0,
        });
        isShowMessageBox = true;
      }
    }, 5000);
  }

  if (checkingStatusInterval) {
    clearInterval(checkingStatusInterval);
  }

  checkingStatusInterval = setInterval(() => {
    if (isGraphQLWebsocketConnected()) {
      notifyWebsocketReconnected();
    }
  }, CHECK_STATUS_INTERVAL_TIME);

  // Rate-limit websocket error reports to avoid quota burn on flapping connections (common client-side noise).
  // Only capture the first occurrence per page load + occasional samples.
  if (! (window as any).__wsErrorCaptured) {
    Sentry.captureException(error);
    (window as any).__wsErrorCaptured = true;
    setTimeout(() => { (window as any).__wsErrorCaptured = false; }, 30000); // allow re-sample after 30s
  }
};

/**
 * Displays a success notification when WebSocket connection is restored.
 * Closes the reconnecting message and clears the status check interval.
 *
 * @example
 * notifyWebsocketReconnected();
 */
export const notifyWebsocketReconnected = () => {
  if (isShowMessageBox) {
    reconnectingMessageBox.close();
    isShowMessageBox = false;
    connectedMessageBox = Message({
      message: WEBSOCKET_CONNECTED_MSG,
      type: "success",
    });
  }

  if (checkingStatusInterval) {
    clearInterval(checkingStatusInterval);
  }
};
